import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { requestPasswordReset } from "@/lib/auth";
import {
  AuthShell,
  Field,
  GoldButton,
  authInputClass,
  errorText,
} from "@/components/auth/AuthShell";

const schema = z.object({ email: z.string().trim().email("Enter a valid email") });

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot Password — CB16" },
      { name: "description", content: "Reset your CB16 account password." },
      { property: "og:title", content: "Forgot Password — CB16" },
      { property: "og:description", content: "Reset your CB16 account password." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ForgotPage,
});

function ForgotPage() {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ email?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      setErrors({ email: parsed.error.flatten().fieldErrors.email?.[0] });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await requestPasswordReset(parsed.data.email);
    setLoading(false);
    if (error) {
      setErrors({ form: error });
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle="If an account exists, we've sent reset instructions."
      >
        <p className="text-center text-sm">
          <Link to="/login" className="text-[#C6A15B] hover:underline">
            Back to sign in
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot password" subtitle="We'll send you a reset link">
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Field label="Email" htmlFor="email" error={errors.email}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputClass}
          />
        </Field>
        {errors.form && <p className={errorText}>{errors.form}</p>}
        <GoldButton type="submit" disabled={loading}>
          {loading ? "Sending…" : "Send Reset Link"}
        </GoldButton>
        <p className="text-center text-xs tracking-wide text-black/70">
          <Link to="/login" className="hover:text-[#C6A15B]">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
